import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  anchorCitations,
  citationGroups,
  type SummaryCitationAnchor,
} from "../../TimelineItem/utils/citationMarkers";
import {
  normalizeMarkdownToPlainText,
  textPosToDocPos,
} from "../utils/diffUtils";

export const citationDecorationPluginKey = new PluginKey("citationDecorations");

export interface CitationDecorationConfig {
  citations: readonly SummaryCitationAnchor[];
  /**
   * Host element for one claim's markers, keyed by the group's `key`. The
   * caller owns these nodes and renders the marker into them, so the same
   * React component draws the markers in both display and edit mode.
   */
  hostFor: (key: string) => HTMLElement;
}

/**
 * `citations` places the markers, or reconciles them to a changed list once
 * they are placed; `clear` tears them down and returns the plugin to its
 * unplaced state, so re-enabling resolves anchors afresh.
 */
export type CitationDecorationMeta =
  | ({ kind: "citations" } & CitationDecorationConfig)
  | { kind: "clear" };

interface CitationDecorationState {
  decorations: DecorationSet;
  /**
   * `SummaryCitation.id` → the number rendered for it, fixed when the markers
   * were placed. Frozen deliberately: renumbering as the document changes would
   * make a claim's number shift, or drop away entirely, while it is being
   * edited — and the number is how a reviewer refers to a source.
   */
  numbers: Map<string, number>;
  config: CitationDecorationConfig | null;
}

function buildDecorations(
  doc: ProseMirrorNode,
  config: CitationDecorationConfig,
  posOf: (textOffset: number) => number,
): { decorations: DecorationSet; numbers: Map<string, number> } {
  if (config.citations.length === 0) {
    return { decorations: DecorationSet.empty, numbers: new Map() };
  }

  // The stored anchor is markdown; the editor holds plain text. Normalizing the
  // needle rather than the haystack keeps offsets in the editor's own space, so
  // they map straight to document positions.
  const { groups, numbers } = anchorCitations(
    doc.textContent,
    config.citations,
    normalizeMarkdownToPlainText,
  );

  const decorations = DecorationSet.create(
    doc,
    groups.flatMap((group) => [
      // An invisible companion spanning the claim itself. ProseMirror drops an
      // inline decoration once its range is emptied, which is what tells the
      // widget its claim is gone -- a widget alone sits at a single position and
      // would survive the deletion of every character around it, leaving a
      // marker pointing at nothing.
      Decoration.inline(
        // Mapped from the claim's own start offset rather than by subtracting
        // its length from the end: node boundaries carry positions that the
        // concatenated text does not, so the two only agree within one block.
        posOf(group.endsAt - group.length),
        posOf(group.endsAt - 1) + 1,
        {},
        { citationKey: group.key, isCitationRange: true },
      ),
      // Map the claim's last character and step past it, rather than mapping
      // the position after it: an offset sitting exactly on a text node's
      // boundary resolves to the *next* node, which put the marker at the head
      // of the following paragraph instead of the tail of this one.
      Decoration.widget(
        posOf(group.endsAt - 1) + 1,
        () => config.hostFor(group.key),
        {
          // Sits after the claim's last character rather than before whatever
          // follows it, so typing at the boundary extends the text and not the
          // marker.
          side: 1,
          // Widgets are not part of the document: without this the marker would
          // be copied out as an empty node and counted as a selectable position.
          ignoreSelection: true,
          // The marker's `[1]` is a real focusable button, and `contentEditable`
          // only stops the *browser* editing inside it -- ProseMirror's own
          // handlers still see the event. Without this, Enter on a
          // keyboard-focused marker splits the block behind the menu and Space
          // types into the summary: chrome mutating the text a save writes back.
          stopEvent: () => true,
          key: group.key,
          citationKey: group.key,
        },
      ),
    ]),
  );
  return { decorations, numbers };
}

/**
 * Drop the markers whose citations are gone, and renumber what is left.
 *
 * Positional, taken from the decorations already on screen rather than by
 * resolving the anchors again: `Remove` refetches the citation rows, so a
 * rebuild here would re-anchor against a document the reviewer has since
 * edited and silently drop the marker on every claim they had touched --
 * exactly what mapping the decorations exists to prevent. Numbering stays
 * dense and in reading order, so it still agrees with display mode.
 */
function reconcileTo(
  prev: CitationDecorationState,
  citations: readonly SummaryCitationAnchor[],
  doc: ProseMirrorNode,
): { decorations: DecorationSet; numbers: Map<string, number> } {
  const idsByKey = new Map(
    citationGroups(citations).map((group) => [group.key, group.ids]),
  );
  const kept = prev.decorations
    .find()
    .filter((d) => (idsByKey.get(d.spec.citationKey)?.length ?? 0) > 0);

  const numbers = new Map<string, number>();
  let next = 1;
  const inReadingOrder = kept
    .filter((d) => !d.spec.isCitationRange)
    .sort((a, b) => a.from - b.from);
  for (const widget of inReadingOrder) {
    for (const id of idsByKey.get(widget.spec.citationKey) ?? []) {
      numbers.set(id, next);
      next += 1;
    }
  }

  return { decorations: DecorationSet.create(doc, kept), numbers };
}

/**
 * Render `[1][2]` source markers in the editor as ProseMirror decorations.
 *
 * Decorations, not text: the editor's content is what a save writes back to
 * `RecordBriefingSegment.text`, so a marker injected into the document would be
 * persisted into the summary itself — the failure the sidecar data model exists
 * to avoid. A decoration is invisible to the document model, so it cannot be
 * typed over, serialized, or saved. Precedent: `SearchHighlightExtension`.
 *
 * Once placed, markers ride the edit through `DecorationSet.map`, which follows
 * ProseMirror's real operations instead of re-running a text search on every
 * keystroke. A marker therefore stays on its claim while it is being edited,
 * and disappears exactly when the text it is attached to is deleted.
 *
 * Usage — dispatch a transaction carrying the config to place them:
 * ```ts
 * editor.view.dispatch(
 *   editor.state.tr.setMeta(citationDecorationPluginKey, {
 *     kind: "citations",
 *     citations,
 *     hostFor,
 *   }),
 * );
 * ```
 * Dispatching it again with a changed list reconciles the markers already
 * placed; `{ kind: "clear" }` removes them and returns the plugin to unplaced.
 */
export const CitationDecorationExtension = Extension.create({
  name: "citationDecorations",

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: citationDecorationPluginKey,

        state: {
          init(): CitationDecorationState {
            return {
              decorations: DecorationSet.empty,
              numbers: new Map(),
              config: null,
            };
          },

          apply(tr, prev, _oldState, newState): CitationDecorationState {
            const meta = tr.getMeta(citationDecorationPluginKey) as
              | CitationDecorationMeta
              | undefined;

            if (meta?.kind === "clear") {
              return {
                decorations: DecorationSet.empty,
                numbers: new Map(),
                config: null,
              };
            }

            if (meta?.kind === "citations") {
              // Unplaced -- resolve the anchors against the text.
              if (!prev.config) {
                return {
                  config: meta,
                  ...buildDecorations(newState.doc, meta, (offset) =>
                    textPosToDocPos(editor, offset),
                  ),
                };
              }
              // Already placed: the list changed under markers that are riding
              // an edit, so reconcile rather than resolve again.
              return {
                config: meta,
                ...reconcileTo(prev, meta.citations, newState.doc),
              };
            }

            if (tr.docChanged) {
              // Map, don't rebuild. Re-anchoring mid-edit would make markers
              // jump to another copy of a half-typed claim, and re-resolving a
              // claim the user is partway through rewriting would drop its
              // marker and bring it back on the next keystroke.
              const mapped = prev.decorations
                .map(tr.mapping, newState.doc)
                .find();
              const claimsWithTextLeft = new Set(
                mapped
                  .filter((d) => d.spec.isCitationRange)
                  .map((d) => d.spec.citationKey),
              );
              return {
                ...prev,
                decorations: DecorationSet.create(
                  newState.doc,
                  mapped.filter((d) =>
                    claimsWithTextLeft.has(d.spec.citationKey),
                  ),
                ),
              };
            }

            return prev;
          },
        },

        props: {
          decorations(state) {
            const pluginState = citationDecorationPluginKey.getState(state) as
              | CitationDecorationState
              | undefined;
            return pluginState?.decorations ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },
});
